import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
} from "@mui/material";
import closeIcon from "../../../assets/images/close_icon.svg";
import pdfFileIcon from "../../../assets/images/pdf-file-icon.svg";
import warningTriangleIcon from "../../../assets/images/warning-triangle.svg";
import formatSchoolAddress from "../../utils/formatSchoolAddress";
import styles from "./OmrUploadDialog.module.css";
import DialogMotionTransition from "./DialogMotionTransition";
import useOmrUploadData from "../../../store/omrUpload/useOmrUploadData";

const OmrUploadDialog = ({ isOpen, onClose, onSuccess }) => {
  const { selectedSchool, selectedEvent, grades, uploadOmrPdf, uploadState } =
    useOmrUploadData({ fetchUploads: false });
  const [selectedGrade, setSelectedGrade] = useState("");
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);
  const mutationResetRef = useRef(uploadState.reset);

  useEffect(() => {
    if (isOpen && selectedEvent) {
      setSelectedGrade("");
      setFile(null);
      setError("");
      setUploadProgress(0);
      mutationResetRef.current?.();
    }
  }, [isOpen, selectedEvent]);

  useEffect(() => {
    mutationResetRef.current = uploadState.reset;
  }, [uploadState.reset]);

  useEffect(() => {
    setUploadProgress(uploadState.progress || 0);
  }, [uploadState.progress]);

  useEffect(() => {
    if (uploadState.isError) {
      setError(
        uploadState.error?.data?.message ||
          uploadState.error?.message ||
          "Unable to upload the file."
      );
    }
  }, [uploadState.isError, uploadState.error]);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Validate file type
    if (selectedFile.type !== "application/pdf") {
      setError("Invalid file type. Only PDF files are allowed.");
      setFile(null);
      return;
    }

    // Validate file size (100 MB)
    const maxSize = 100 * 1024 * 1024;
    if (selectedFile.size > maxSize) {
      setError("File size exceeds 100 MB limit.");
      setFile(null);
      return;
    }

    setFile(selectedFile);
    setError("");
  };

  const handleUpload = async () => {
    if (!selectedGrade || !file || !selectedEvent) {
      setError("Please select a grade and file.");
      return;
    }

    setError("");

    try {
      await uploadOmrPdf({
        gradeId: selectedGrade,
        file,
      });
      setUploadProgress(100);
      onSuccess();
      handleClose();
    } catch (uploadError) {
      setError(
        uploadError?.data?.message ||
          uploadError?.message ||
          "Unable to upload the file."
      );
    }
  };

  const handleClose = () => {
    if (uploadState.isLoading) return; // Prevent closing during upload
    setSelectedGrade("");
    setFile(null);
    setError("");
    setUploadProgress(0);
    mutationResetRef.current?.();
    onClose();
  };

  const schoolAddress = formatSchoolAddress(selectedSchool);
  const schoolId = selectedSchool?.school_id || selectedSchool?.id || "N/A";
  const eventName = selectedEvent?.event || "N/A";

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={window.innerWidth < 768}
      disableEscapeKeyDown={uploadState.isLoading}
      slots={{ transition: DialogMotionTransition }}
      slotProps={{
        paper: { className: styles.dialogPaper },
      }}
    >
      <DialogTitle className={styles.modalHeader}>
        <div className={styles.headerTextGroup}>
          <h2 className={styles.modalTitle}>Upload OMR PDF</h2>
        </div>
        <button
          type="button"
          className={styles.closeButton}
          onClick={handleClose}
          aria-label="Close"
          disabled={uploadState.isLoading}
        >
          <img src={closeIcon} alt="" aria-hidden="true" />
        </button>
      </DialogTitle>
      <DialogContent className={styles.modalContent}>
        <div className={styles.detailsCard}>
          <div className={styles.detailsList}>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>School</span>
              <span className={styles.detailValue}>{schoolAddress}</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>School ID</span>
              <span className={styles.detailValue}>{schoolId}</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Event</span>
              <span className={styles.detailValue}>{eventName}</span>
            </div>
          </div>
        </div>

        <div className={styles.formSection}>
          <div className={styles.formCard}>
            <label className={styles.fieldLabel} htmlFor="grade-select">
              Select Grade
            </label>
            <select
              id="grade-select"
              className={styles.selectInput}
              value={selectedGrade}
              onChange={(event) => setSelectedGrade(event.target.value)}
              disabled={uploadState.isLoading || grades.length === 0}
            >
              <option value="" disabled>
                Choose a grade
              </option>
              {grades.map((grade) => (
                <option key={grade} value={grade.toString()}>
                  Grade {grade}
                </option>
              ))}
            </select>

            <div className={styles.fileUploadSection}>
              <span className={styles.fieldLabel}>Upload PDF</span>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                onChange={handleFileSelect}
                disabled={uploadState.isLoading}
                className={styles.fileInput}
              />
              <button
                type="button"
                className={`${styles.actionButton} ${styles.primaryAction}`}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadState.isLoading}
              >
                Select File
              </button>
            </div>

            {error && (
              <Alert severity="error" className={styles.errorAlert}>
                {error}
              </Alert>
            )}
          </div>

          {file && (
            <div className={styles.previewCard}>
              <div className={styles.pdfIconContainer}>
                {uploadState.isLoading && (
                  <div className={styles.uploadWarning}>
                    <img
                      src={warningTriangleIcon}
                      alt=""
                      aria-hidden="true"
                      className={styles.uploadWarningIcon}
                    />
                    <span>
                      Please wait on the screen and do not go back until the
                      upload is completed.
                    </span>
                  </div>
                )}
                <div className={styles.pdfThumbnail}>
                  <img src={pdfFileIcon} alt="PDF" className={styles.pdfIcon} />
                  {uploadState.isLoading && (
                    <div className={styles.progressOverlay}>
                      <CircularProgress
                        variant="determinate"
                        value={uploadProgress}
                        size={60}
                        thickness={4}
                      />
                    </div>
                  )}
                </div>
                <p className={styles.fileMeta}>
                  {file.name}
                  <span> · {(file.size / (1024 * 1024)).toFixed(2)} MB</span>
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions className={styles.modalFooter}>
        <button
          type="button"
          className={`${styles.actionButton} ${styles.secondaryAction}`}
          onClick={handleClose}
          disabled={uploadState.isLoading}
        >
          Cancel
        </button>
        <button
          type="button"
          className={`${styles.actionButton} ${styles.primaryAction}`}
          onClick={handleUpload}
          disabled={!selectedGrade || !file || uploadState.isLoading}
        >
          {uploadState.isLoading ? "Uploading..." : "Upload"}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default OmrUploadDialog;
